import { useEffect, useMemo, useState } from 'react';
import {
  Link,
  Route,
  Routes,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  Activity,
  ArrowLeft,
  Award,
  Briefcase,
  Calendar,
  CheckCircle2,
  ChevronRight,
  Circle,
  Clock,
  DollarSign,
  Edit,
  Filter,
  FileBarChart,
  Globe,
  Mail,
  MapPin,
  Phone,
  Plus,
  Printer,
  Save,
  Sparkles,
  Star,
  Trash2,
  TrendingUp,
  Users,
  X,
} from 'lucide-react';
import { api } from '../../api/client';
import EmptyState from '../../components/EmptyState';
import InfoTip from '../../components/InfoTip';
import ErrorBanner from '../../components/ErrorBanner';
import { SkeletonList, SkeletonHero, SkeletonGrid, SkeletonTable } from '../../components/Skeleton';
import { toast } from '../../lib/toast';
import { formatDate, formatDateTime, formatNumber, formatRelative } from '../../lib/format';

const STATUS_META = {
  lead: { label: 'Lead', class: 'bg-gray-100 text-gray-700', icon: Circle },
  onboarding: { label: 'Onboarding', class: 'bg-amber-50 text-amber-700', icon: Clock },
  active: { label: 'Active', class: 'bg-emerald-50 text-emerald-700', icon: CheckCircle2 },
  paused: { label: 'Paused', class: 'bg-gray-100 text-gray-500', icon: Circle },
  churned: { label: 'Churned', class: 'bg-rose-50 text-rose-700', icon: X },
};

const TIER_META = {
  hot: { label: 'Hot', class: 'bg-rose-50 text-rose-700' },
  warm: { label: 'Warm', class: 'bg-amber-50 text-amber-700' },
  cool: { label: 'Cool', class: 'bg-cyan-500/10 text-cyan-700' },
  cold: { label: 'Cold', class: 'bg-gray-100 text-gray-600' },
};

const PLAN_LABELS = {
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
  platinum: 'Platinum',
};

const INVOICE_STATUS_META = {
  draft: { label: 'Draft', class: 'bg-gray-100 text-gray-700' },
  sent: { label: 'Sent', class: 'bg-brand-50 text-brand-700' },
  paid: { label: 'Paid', class: 'bg-emerald-50 text-emerald-700' },
  overdue: { label: 'Overdue', class: 'bg-rose-50 text-rose-700' },
  void: { label: 'Void', class: 'bg-gray-100 text-gray-500' },
};

const CURRENCY_SYMBOL = { USD: '$', EUR: '€', GBP: '£', INR: '₹' };
const currencySym = (c) => CURRENCY_SYMBOL[c] || c || '$';

export default function ClientsTab() {
  return (
    <Routes>
      <Route index element={<ClientsList />} />
      <Route path=":clientId" element={<ClientDetail />} />
    </Routes>
  );
}

/* ---------- List view ---------- */

function ClientsList() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [filter, setFilter] = useState('all');
  const [adding, setAdding] = useState(false);

  const clientsQuery = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.get('/api/clients').then((r) => r.data.clients),
  });

  const summaryQuery = useQuery({
    queryKey: ['client-summary'],
    queryFn: () => api.get('/api/clients/summary').then((r) => r.data),
  });

  const createMutation = useMutation({
    mutationFn: (body) =>
      api.post('/api/clients', body).then((r) => r.data.client),
    onSuccess: (client) => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['client-summary'] });
      setAdding(false);
      toast.success(`Added ${client.name || 'client'}`);
      navigate(client._id);
    },
    onError: (err) => toast.error(err, 'Could not add client'),
  });

  const clients = clientsQuery.data || [];
  const summary = summaryQuery.data;

  const filtered = filter === 'all' ? clients : clients.filter((c) => c.status === filter);
  const sorted = useMemo(
    () => [...filtered].sort((a, b) => (b.lead_score || 0) - (a.lead_score || 0)),
    [filtered],
  );

  return (
    <div className="space-y-6">
      <ClientsHero summary={summary} onAdd={() => setAdding(true)} />

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <SummaryTile
            icon={Users}
            label="Active clients"
            value={formatNumber(summary.counts.active || 0)}
            hint={`${summary.counts.total} total`}
          />
          <SummaryTile
            icon={DollarSign}
            label="Active MRR"
            value={`$${formatNumber(summary.mrr.active || 0)}`}
            hint={`Total $${formatNumber(summary.mrr.total || 0)}`}
          />
          <SummaryTile
            icon={Sparkles}
            label="Leads"
            value={formatNumber(summary.counts.lead || 0)}
          />
          <SummaryTile
            icon={Clock}
            label="Onboarding"
            value={formatNumber(summary.counts.onboarding || 0)}
          />
        </div>
      )}

      {createMutation.isError && (
        <div className="rounded-xl border border-rose-100 bg-rose-50 text-rose-700 text-sm p-3">
          {createMutation.error?.response?.data?.error || 'Could not create client'}
        </div>
      )}

      {adding && (
        <CreateClientCard
          onCancel={() => setAdding(false)}
          onSubmit={(body) => createMutation.mutate(body)}
          pending={createMutation.isPending}
        />
      )}

      <StatusFilter filter={filter} setFilter={setFilter} counts={summary?.counts} />

      {clientsQuery.isLoading ? (
        <SkeletonList rows={4} />
      ) : !clients.length ? (
        <EmptyState
          icon={Briefcase}
          title="No clients yet"
          body="Track every prospect, lead, and active engagement in one place — with a transparent lead score so you know where to focus."
          action={
            <button className="btn-primary" onClick={() => setAdding(true)}>
              <Plus className="w-4 h-4" /> Add first client
            </button>
          }
        />
      ) : !sorted.length ? (
        <div className="card text-sm text-ink-400">
          No clients at this status.
        </div>
      ) : (
        <div className="card">
          <ul className="divide-y divide-gray-50">
            {sorted.map((c) => (
              <ClientRow key={c._id} client={c} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ClientsHero({ summary, onAdd }) {
  return (
    <div className="relative rounded-2xl overflow-hidden bg-ink-gradient text-white p-6 sm:p-8 shadow-xl">
      <div className="absolute inset-0 opacity-30 bg-brand-radial" />
      <div className="absolute inset-0 bg-grid opacity-20" />
      <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-brand-600 blur-3xl opacity-30" />
      <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-white/60 mb-1 flex items-center gap-1.5">
            <Briefcase className="w-3 h-3" /> Clients
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold inline-flex items-center gap-2 flex-wrap">
            {summary && summary.counts.total > 0
              ? `${summary.counts.total} client${summary.counts.total === 1 ? '' : 's'} · $${formatNumber(summary.mrr.active || 0)} active MRR`
              : 'Run your agency book from here'}
            <span className="text-white/70">
              <InfoTip
                title="What's tracked here"
                body="Every client carries a status (Lead → Onboarding → Active / Paused / Churned), a temperature tier (Hot / Warm / Cool / Cold), and an MRR. Invoices live under each client. MRR shown is the sum of all Active clients."
              />
            </span>
          </h2>
          <p className="text-sm text-white/70 mt-1 max-w-xl">
            Pipeline view of leads, onboardings, and active engagements with rule-based
            lead scoring. Generate invoices from the same place.
          </p>
        </div>
        <button
          onClick={onAdd}
          className="inline-flex items-center gap-2 rounded-lg bg-white text-ink-900 px-4 py-2 text-sm font-semibold hover:bg-white/90 transition-colors"
        >
          <Plus className="w-4 h-4" /> New client
        </button>
      </div>
    </div>
  );
}

function SummaryTile({ icon: Icon, label, value, hint }) {
  return (
    <div className="card-compact relative overflow-hidden">
      <div className="absolute -top-8 -right-8 h-24 w-24 rounded-full bg-gradient-to-br from-brand-50 to-brand-100/40 opacity-60 blur-2xl pointer-events-none" />
      <div className="relative flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">
            {label}
          </div>
          <div className="text-2xl font-bold text-ink-900 mt-0.5">{value}</div>
          {hint && <div className="text-xs text-ink-400 mt-0.5">{hint}</div>}
        </div>
        <span className="flex-none h-9 w-9 rounded-lg bg-gradient-to-br from-brand-50 to-cyan-500/10 text-brand-700 flex items-center justify-center">
          <Icon className="w-4 h-4" />
        </span>
      </div>
    </div>
  );
}

function StatusFilter({ filter, setFilter, counts }) {
  const tabs = [
    { id: 'all', label: 'All', count: counts?.total || 0 },
    ...Object.entries(STATUS_META).map(([id, meta]) => ({
      id,
      label: meta.label,
      count: counts?.[id] || 0,
    })),
  ];
  return (
    <div className="flex gap-1.5 overflow-x-auto scroll-soft py-1">
      <Filter className="w-3.5 h-3.5 text-ink-400 mt-1 flex-none" />
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => setFilter(t.id)}
          className={clsx(
            'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold whitespace-nowrap transition-colors border',
            filter === t.id
              ? 'bg-brand-50 text-brand-700 border-brand-200'
              : 'bg-white text-ink-700 border-gray-200 hover:border-brand-200',
          )}
        >
          {t.label}
          <span className="text-ink-400">{t.count}</span>
        </button>
      ))}
    </div>
  );
}

function ClientRow({ client }) {
  const status = STATUS_META[client.status] || STATUS_META.lead;
  const tier = TIER_META[client.lead_tier] || TIER_META.cold;
  const StatusIcon = status.icon;
  return (
    <li className="py-3">
      <Link
        to={client._id}
        className="flex items-center justify-between gap-3 hover:bg-gray-50/60 -mx-2 px-2 py-1 rounded-lg"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <span className="flex-none h-10 w-10 rounded-xl bg-gradient-to-br from-brand-500 to-cyan-500 text-white flex items-center justify-center font-semibold text-sm">
            {(client.company_name || client.name || '?').charAt(0).toUpperCase()}
          </span>
          <div className="min-w-0">
            <div className="font-medium text-ink-900 truncate flex items-center gap-2">
              {client.company_name || client.name}
              {client.company_name && client.name && (
                <span className="text-xs text-ink-400 truncate">· {client.name}</span>
              )}
            </div>
            <div className="text-xs text-ink-400 truncate flex items-center gap-2 flex-wrap">
              {client.email && (
                <span className="inline-flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  {client.email}
                </span>
              )}
              {client.website && (
                <span className="inline-flex items-center gap-1">
                  <Globe className="w-3 h-3" />
                  {client.website.replace(/^https?:\/\//, '')}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-none">
          <span className={`pill ${tier.class}`} title={`Lead score ${client.lead_score}/100`}>
            <Star className="w-3 h-3" /> {client.lead_score || 0}
          </span>
          {client.mrr > 0 && (
            <span className="text-xs text-ink-700 font-medium tabular-nums">
              {currencySym(client.currency)}
              {formatNumber(client.mrr)}/mo
            </span>
          )}
          <span className={`pill ${status.class}`}>
            <StatusIcon className="w-3 h-3" />
            {status.label}
          </span>
          <ChevronRight className="w-4 h-4 text-ink-400" />
        </div>
      </Link>
    </li>
  );
}

function CreateClientCard({ onCancel, onSubmit, pending }) {
  const [form, setForm] = useState({
    name: '',
    company_name: '',
    email: '',
    website: '',
    status: 'lead',
  });
  const submit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };
  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <Plus className="w-4 h-4" />
          </span>
          New client
        </div>
        <button className="btn-icon" onClick={onCancel} title="Cancel">
          <X className="w-4 h-4" />
        </button>
      </div>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="label">Contact name</label>
          <input
            className="input"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Alex Smith"
          />
        </div>
        <div>
          <label className="label">Company</label>
          <input
            className="input"
            value={form.company_name}
            onChange={(e) => setForm({ ...form, company_name: e.target.value })}
            placeholder="Bright Dental"
          />
        </div>
        <div>
          <label className="label">Email</label>
          <input
            className="input"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="alex@brightdental.com"
          />
        </div>
        <div>
          <label className="label">Website</label>
          <input
            className="input"
            value={form.website}
            onChange={(e) => setForm({ ...form, website: e.target.value })}
            placeholder="brightdental.com"
          />
        </div>
        <div>
          <label className="label">Status</label>
          <select
            className="input"
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
          >
            {Object.entries(STATUS_META).map(([id, m]) => (
              <option key={id} value={id}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={pending}>
            <Plus className="w-4 h-4" /> {pending ? 'Creating…' : 'Create client'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ---------- Detail view ---------- */

function ClientDetail() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const clientQuery = useQuery({
    queryKey: ['client', clientId],
    queryFn: () => api.get(`/api/clients/${clientId}`).then((r) => r.data.client),
  });

  const invoicesQuery = useQuery({
    queryKey: ['client-invoices', clientId],
    queryFn: () =>
      api.get(`/api/invoices?client_id=${clientId}`).then((r) => r.data.invoices),
  });

  const updateMutation = useMutation({
    mutationFn: (body) =>
      api.put(`/api/clients/${clientId}`, body).then((r) => r.data.client),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client', clientId] });
      toast.success('Client updated');
    },
    onError: (err) => toast.error(err, 'Could not update client'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/api/clients/${clientId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['client-summary'] });
      toast.success('Client deleted');
      navigate('..');
    },
    onError: (err) => toast.error(err, 'Could not delete client'),
  });

  const createInvoiceMutation = useMutation({
    mutationFn: () =>
      api
        .post('/api/invoices', {
          client_id: clientId,
          line_items: [{ description: 'Monthly AEO services', qty: 1, unit_price: 0 }],
          tax_rate: 0,
        })
        .then((r) => r.data.invoice),
    onSuccess: (invoice) => {
      qc.invalidateQueries({ queryKey: ['client-invoices', clientId] });
      toast.success('Invoice created');
      navigate(`/invoices/${invoice._id}`);
    },
    onError: (err) => toast.error(err, 'Could not create invoice'),
  });

  const client = clientQuery.data;
  const invoices = invoicesQuery.data || [];

  if (clientQuery.isLoading) {
    return (
      <div className="space-y-6">
        <SkeletonHero />
        <SkeletonGrid count={4} />
        <SkeletonTable rows={5} cols={5} />
      </div>
    );
  }
  if (!client) {
    return (
      <EmptyState
        icon={Briefcase}
        title="Client not found"
        action={
          <Link to=".." className="btn-secondary">
            <ArrowLeft className="w-4 h-4" /> Back to clients
          </Link>
        }
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Link to=".." className="btn-icon">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <span className="flex-none h-12 w-12 rounded-2xl bg-gradient-to-br from-brand-500 to-cyan-500 text-white flex items-center justify-center font-bold text-lg">
            {(client.company_name || client.name || '?').charAt(0).toUpperCase()}
          </span>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-ink-400">Client</div>
            <h2 className="text-xl font-bold text-ink-900 truncate">
              {client.company_name || client.name}
            </h2>
            {client.company_name && (
              <div className="text-xs text-ink-400 truncate">{client.name}</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="btn-secondary"
            onClick={() => createInvoiceMutation.mutate()}
            disabled={createInvoiceMutation.isPending}
          >
            <FileBarChart className="w-4 h-4" />
            {createInvoiceMutation.isPending ? 'Creating…' : 'New invoice'}
          </button>
          <button
            className="btn-icon hover:!text-rose-600 hover:!bg-rose-50"
            onClick={() => {
              if (
                window.confirm(
                  `Delete ${client.company_name || client.name}? This also deletes their invoices.`,
                )
              ) {
                deleteMutation.mutate();
              }
            }}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <ClientHeader client={client} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ContactCard client={client} onSave={(body) => updateMutation.mutate(body)} saving={updateMutation.isPending} />
        <PlanCard client={client} onSave={(body) => updateMutation.mutate(body)} saving={updateMutation.isPending} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <LeadScoreCard client={client} onSave={(body) => updateMutation.mutate(body)} saving={updateMutation.isPending} />
        <OnboardingChecklistCard client={client} onSave={(body) => updateMutation.mutate(body)} saving={updateMutation.isPending} />
      </div>

      <NotesCard client={client} onSave={(body) => updateMutation.mutate(body)} saving={updateMutation.isPending} />

      <InvoicesCard
        invoices={invoices}
        loading={invoicesQuery.isLoading}
        onCreate={() => createInvoiceMutation.mutate()}
        creating={createInvoiceMutation.isPending}
      />
    </div>
  );
}

function ClientHeader({ client }) {
  const status = STATUS_META[client.status] || STATUS_META.lead;
  const tier = TIER_META[client.lead_tier] || TIER_META.cold;
  const StatusIcon = status.icon;
  return (
    <div className="card flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-3 flex-wrap">
        <span className={`pill ${status.class}`}>
          <StatusIcon className="w-3 h-3" />
          {status.label}
        </span>
        <span className={`pill ${tier.class}`}>
          <Star className="w-3 h-3" /> Lead score {client.lead_score}/100 · {tier.label}
        </span>
        {client.plan && (
          <span className="pill-brand">{PLAN_LABELS[client.plan] || client.plan}</span>
        )}
        {client.mrr > 0 && (
          <span className="text-sm font-semibold text-ink-900 tabular-nums">
            {currencySym(client.currency)}
            {formatNumber(client.mrr)} / {client.billing_cadence}
          </span>
        )}
      </div>
      <div className="text-xs text-ink-400">
        Updated {formatRelative(client.updatedAt)}
      </div>
    </div>
  );
}

function ContactCard({ client, onSave, saving }) {
  const [form, setForm] = useState({
    name: client.name || '',
    company_name: client.company_name || '',
    email: client.email || '',
    phone: client.phone || '',
    website: client.website || '',
    status: client.status,
  });
  const [savedFlash, setSavedFlash] = useState(false);
  useEffect(() => {
    setForm({
      name: client.name || '',
      company_name: client.company_name || '',
      email: client.email || '',
      phone: client.phone || '',
      website: client.website || '',
      status: client.status,
    });
  }, [client]);

  const submit = (e) => {
    e.preventDefault();
    onSave(form);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 2000);
  };

  return (
    <form onSubmit={submit} className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <Users className="w-4 h-4" />
          </span>
          Contact
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="label">Contact name</label>
          <input
            className="input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="label">Company</label>
          <input
            className="input"
            value={form.company_name}
            onChange={(e) => setForm({ ...form, company_name: e.target.value })}
          />
        </div>
        <div>
          <label className="label">Email</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
            <input
              className="input pl-10"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>
        </div>
        <div>
          <label className="label">Phone</label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
            <input
              className="input pl-10"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className="label">Website</label>
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
            <input
              className="input pl-10"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
            />
          </div>
        </div>
        <div>
          <label className="label">Status</label>
          <select
            className="input"
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
          >
            {Object.entries(STATUS_META).map(([id, m]) => (
              <option key={id} value={id}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <button className="btn-primary w-full" disabled={saving}>
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save contact'}
          </button>
        </div>
        {savedFlash && (
          <div className="sm:col-span-2 text-xs text-emerald-700 inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" /> Saved.
          </div>
        )}
      </div>
    </form>
  );
}

function PlanCard({ client, onSave, saving }) {
  const [form, setForm] = useState({
    plan: client.plan || 'bronze',
    mrr: client.mrr || 0,
    currency: client.currency || 'USD',
    billing_cadence: client.billing_cadence || 'monthly',
    start_date: client.start_date
      ? new Date(client.start_date).toISOString().slice(0, 10)
      : '',
  });
  useEffect(() => {
    setForm({
      plan: client.plan || 'bronze',
      mrr: client.mrr || 0,
      currency: client.currency || 'USD',
      billing_cadence: client.billing_cadence || 'monthly',
      start_date: client.start_date
        ? new Date(client.start_date).toISOString().slice(0, 10)
        : '',
    });
  }, [client]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave({ ...form, mrr: Number(form.mrr) || 0 });
      }}
      className="card"
    >
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <DollarSign className="w-4 h-4" />
          </span>
          Plan & billing
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="label">Plan</label>
          <select
            className="input"
            value={form.plan}
            onChange={(e) => setForm({ ...form, plan: e.target.value })}
          >
            {Object.entries(PLAN_LABELS).map(([id, label]) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Cadence</label>
          <select
            className="input"
            value={form.billing_cadence}
            onChange={(e) => setForm({ ...form, billing_cadence: e.target.value })}
          >
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annual">Annual</option>
          </select>
        </div>
        <div>
          <label className="label">MRR</label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
            <input
              className="input pl-10"
              type="number"
              min="0"
              step="any"
              value={form.mrr}
              onChange={(e) => setForm({ ...form, mrr: e.target.value })}
            />
          </div>
        </div>
        <div>
          <label className="label">Currency</label>
          <select
            className="input"
            value={form.currency}
            onChange={(e) => setForm({ ...form, currency: e.target.value })}
          >
            <option value="USD">USD ($)</option>
            <option value="EUR">EUR (€)</option>
            <option value="GBP">GBP (£)</option>
            <option value="INR">INR (₹)</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="label">Start date</label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
            <input
              className="input pl-10"
              type="date"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
            />
          </div>
        </div>
        <div className="sm:col-span-2">
          <button className="btn-primary w-full" disabled={saving}>
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save plan'}
          </button>
        </div>
      </div>
    </form>
  );
}

const LEAD_AXES = [
  {
    key: 'budget',
    label: 'Budget',
    options: [
      { value: 'high', label: 'High' },
      { value: 'medium', label: 'Medium' },
      { value: 'low', label: 'Low' },
    ],
  },
  {
    key: 'timeline',
    label: 'Timeline',
    options: [
      { value: 'now', label: 'Now' },
      { value: 'this-quarter', label: 'This quarter' },
      { value: 'someday', label: 'Someday' },
    ],
  },
  {
    key: 'fit',
    label: 'Product fit',
    options: [
      { value: 'high', label: 'High' },
      { value: 'medium', label: 'Medium' },
      { value: 'low', label: 'Low' },
    ],
  },
  {
    key: 'response_speed',
    label: 'Responsiveness',
    options: [
      { value: 'fast', label: 'Fast' },
      { value: 'medium', label: 'Medium' },
      { value: 'slow', label: 'Slow' },
    ],
  },
];

function LeadScoreCard({ client, onSave, saving }) {
  const [inputs, setInputs] = useState(client.lead_inputs || {});
  useEffect(() => {
    setInputs(client.lead_inputs || {});
  }, [client]);
  const tier = TIER_META[client.lead_tier] || TIER_META.cold;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave({ lead_inputs: inputs });
      }}
      className="card"
    >
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <Award className="w-4 h-4" />
          </span>
          Lead score
        </div>
        <span className={`pill ${tier.class}`}>
          {client.lead_score || 0}/100 · {tier.label}
        </span>
      </div>
      <p className="text-xs text-ink-400 mb-3">
        Transparent rules — each axis adds points. Score recomputes when you save.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {LEAD_AXES.map((axis) => (
          <div key={axis.key}>
            <label className="label">{axis.label}</label>
            <select
              className="input"
              value={inputs[axis.key] || ''}
              onChange={(e) =>
                setInputs({ ...inputs, [axis.key]: e.target.value || null })
              }
            >
              <option value="">— Pick —</option>
              {axis.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        ))}
        <label className="sm:col-span-2 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!inputs.decision_maker}
            onChange={(e) =>
              setInputs({ ...inputs, decision_maker: e.target.checked })
            }
            className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500/30"
          />
          Talking to the decision maker
        </label>
        <div className="sm:col-span-2">
          <button className="btn-primary w-full" disabled={saving}>
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Recompute score'}
          </button>
        </div>
      </div>
    </form>
  );
}

function OnboardingChecklistCard({ client, onSave, saving }) {
  const [items, setItems] = useState(client.onboarding_checklist || []);
  useEffect(() => {
    setItems(client.onboarding_checklist || []);
  }, [client]);
  const [newLabel, setNewLabel] = useState('');

  const toggle = (id) => {
    const next = items.map((it) =>
      it.id === id
        ? { ...it, done: !it.done, completed_at: !it.done ? new Date().toISOString() : null }
        : it,
    );
    setItems(next);
    onSave({ onboarding_checklist: next });
  };
  const addItem = () => {
    const label = newLabel.trim();
    if (!label) return;
    const next = [
      ...items,
      { id: `c-${Date.now()}`, label, done: false, completed_at: null },
    ];
    setItems(next);
    setNewLabel('');
    onSave({ onboarding_checklist: next });
  };
  const removeItem = (id) => {
    const next = items.filter((it) => it.id !== id);
    setItems(next);
    onSave({ onboarding_checklist: next });
  };

  const doneCount = items.filter((it) => it.done).length;
  const pct = items.length ? Math.round((doneCount / items.length) * 100) : 0;

  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <CheckCircle2 className="w-4 h-4" />
          </span>
          Onboarding
        </div>
        <span className="text-xs text-ink-400">
          {doneCount}/{items.length} · {pct}%
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden mb-3">
        <div
          className="h-full bg-gradient-to-r from-brand-500 to-cyan-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <ul className="space-y-1.5">
        {items.map((it) => (
          <li
            key={it.id}
            className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-gray-50"
          >
            <button
              type="button"
              className="flex-none"
              onClick={() => toggle(it.id)}
              title={it.done ? 'Mark incomplete' : 'Mark complete'}
            >
              {it.done ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              ) : (
                <Circle className="w-5 h-5 text-ink-400" />
              )}
            </button>
            <span
              className={clsx(
                'text-sm flex-1',
                it.done ? 'line-through text-ink-400' : 'text-ink-900',
              )}
            >
              {it.label}
            </span>
            <button
              type="button"
              className="btn-icon h-7 w-7 hover:!text-rose-600 hover:!bg-rose-50"
              onClick={() => removeItem(it.id)}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </li>
        ))}
      </ul>
      <div className="flex gap-2 mt-3">
        <input
          className="input text-sm"
          placeholder="Add checklist item…"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addItem();
            }
          }}
        />
        <button type="button" className="btn-secondary" onClick={addItem} disabled={saving}>
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>
    </div>
  );
}

function NotesCard({ client, onSave, saving }) {
  const [notes, setNotes] = useState(client.notes || '');
  const [saved, setSaved] = useState(false);
  useEffect(() => setNotes(client.notes || ''), [client]);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave({ notes });
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }}
      className="card"
    >
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <Edit className="w-4 h-4" />
          </span>
          Notes
        </div>
        {saved && (
          <span className="text-xs text-emerald-700 inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" /> Saved.
          </span>
        )}
      </div>
      <textarea
        className="input min-h-[100px]"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Context, conversation history, blockers…"
      />
      <div className="mt-3 flex justify-end">
        <button className="btn-primary" disabled={saving}>
          <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save notes'}
        </button>
      </div>
    </form>
  );
}

function InvoicesCard({ invoices, loading, onCreate, creating }) {
  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <FileBarChart className="w-4 h-4" />
          </span>
          Invoices
        </div>
        <button
          className="btn-secondary"
          onClick={onCreate}
          disabled={creating}
        >
          <Plus className="w-4 h-4" /> {creating ? 'Creating…' : 'New invoice'}
        </button>
      </div>
      {loading ? (
        <SkeletonList rows={3} wrap={false} />
      ) : !invoices.length ? (
        <p className="text-sm text-ink-400">No invoices yet for this client.</p>
      ) : (
        <ul className="divide-y divide-gray-50">
          {invoices.map((inv) => {
            const meta =
              INVOICE_STATUS_META[inv.status] || INVOICE_STATUS_META.draft;
            return (
              <li
                key={inv._id}
                className="py-2.5 flex items-center justify-between gap-3 first:pt-0 last:pb-0"
              >
                <Link
                  to={`/invoices/${inv._id}`}
                  className="flex items-center gap-3 min-w-0 flex-1 hover:opacity-90"
                >
                  <span className="flex-none h-9 w-9 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center">
                    <FileBarChart className="w-4 h-4" />
                  </span>
                  <div className="min-w-0">
                    <div className="font-medium text-ink-900 truncate">{inv.number}</div>
                    <div className="text-xs text-ink-400">
                      {formatDate(inv.issue_date)} · {inv.line_items?.length || 0} items
                    </div>
                  </div>
                </Link>
                <div className="flex items-center gap-3 flex-none">
                  <span className="text-sm font-semibold tabular-nums text-ink-900">
                    {currencySym(inv.currency)}
                    {formatNumber(inv.total || 0)}
                  </span>
                  <span className={`pill ${meta.class}`}>{meta.label}</span>
                  <ChevronRight className="w-4 h-4 text-ink-400" />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
